import { SingularDayDemandBehavior } from '@ebalance/proto/bffuser/v1/singular_day_model_pb'
import type { Control, FieldErrors, UseFormRegister } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  type BoxProps,
  Flex,
  Grid,
  Input,
  TertiaryButton,
} from '@enechain/ecloud-designsystem'

import { CompactCivilDateField } from '~/components/common/CompactCivilDateField'
import { DsSelect, type DsSelectOptions } from '~/components/common/DsSelect'
import {
  FieldErrorText,
  FieldHelperText,
  FieldRoot,
} from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import { MultiSelect } from '~/components/common/MultiSelect'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import type { CivilDate } from '~/models/civilDate'
import { useSingularGroupNameMap } from '~/pages/AdminSingularDay/hooks/useSingularGroupNames'
import {
  SINGULAR_DAY_CATEGORY_OPTIONS,
  SINGULAR_DAY_SPECIAL_GROUP_NUMBERS,
  decodeDemandBehaviorProxy,
  encodeDemandBehaviorProxy,
  resolveSingularGroupName,
  singularDayCategoryLabelKey,
  singularDayDemandBehaviorLabelKey,
} from '~/pages/AdminSingularDay/models/options'
import type { SingularDayFormInput } from '~/pages/AdminSingularDay/models/schema'

type Props = {
  /** create: 日付編集可 / edit: 日付は immutable なので disabled 表示。 */
  mode: 'create' | 'edit'
  register: UseFormRegister<SingularDayFormInput>
  control: Control<SingularDayFormInput>
  errors: FieldErrors<SingularDayFormInput>
  /** 現在の日付値。 `undefined` は未入力。 */
  dateValue: CivilDate | undefined
  /** create モードで日付を編集した時のコールバック。 */
  onDateChange?: (value: CivilDate | undefined) => void
  /**
   * 「特異グループ名を編集」 (一括エディタ) を開く。
   *
   * design (`special-day-calendar.html:192-195`) は**この modal 内の需要扱い
   * select 右端だけ**に集約しており、一覧のツールバーには置かない。
   * 未指定なら導線を出さない (= form 単体で使う時)。
   */
  onEditGroupNames?: () => void
}

/**
 * Grid の cell として振る舞う plain label。 chakra `FieldLabel` は
 * `FieldRoot` context (`useFieldStyles`) を要求するので、 label と input を
 * 別 grid cell に分けたい 2 カラムレイアウトでは使えない。 htmlFor で input
 * と関連付けし、 スタイルは design `.form-label` と揃える (`~/components/
 * common/Field` の FieldLabel の default と同一)。
 */
type GridFormLabelProps = Omit<BoxProps, 'as'> & {
  htmlFor: string
  isTop?: boolean
}
const BoxAsLabel = Box as unknown as React.FC<
  Omit<BoxProps, 'as'> & {
    as?: string
    htmlFor?: string
    children?: React.ReactNode
  }
>
const GridFormLabel = ({
  htmlFor,
  isTop,
  children,
  ...rest
}: GridFormLabelProps): React.ReactElement => (
  <BoxAsLabel
    as="label"
    textStyle="label"
    color="Text.highEmphasis"
    htmlFor={htmlFor}
    {...(isTop === true ? { alignSelf: 'start', paddingTop: '9px' } : {})}
    {...rest}
  >
    {children}
  </BoxAsLabel>
)

/**
 * 特異日登録・編集モーダル共通の form fields (design demand/
 * special-day-calendar.html 準拠: 日付 + 名称 + エリア + カテゴリ + 需要扱い、
 * ラベル左 / 入力右の 2 カラムグリッド)。
 *
 * エリアは `MultiSelect` で複数選択する。 未選択 = 全エリア共通 (AC-07)、
 * 10 エリア全選択は送信時に空へ正規化する (`normalizeAreaCodesForSubmit`)。
 * MultiSelect は value を string で扱うため `AreaCode` (number enum) との
 * 相互変換を行う。
 *
 * カテゴリ / 需要扱いは単一 select なので DS `Select` (= `FormSelect`) を使う
 * (`~/components/common/FormSelect` の JSDoc 参照)。 値は number enum のため
 * option value を `String()` 化し、 form への復元は `numeric` に任せる。
 *
 * design の `.sdc-form` (grid-template-columns: max-content 1fr,
 * align-items: center) を chakra `Grid` で再現 (`SimpleGrid` は
 * `columns` / `minChildWidth` から `gridTemplateColumns` を内部生成する
 * 実装なので、 素の 2 カラム grid が欲しい本用途では `Grid` の方が破綻
 * しにくい)。 複数行になり得るセル (名称・エリア・需要扱い) のラベルは
 * align-self: start + padding-top で入力欄の最初の行に揃える
 * (design `.form-label.is-top` 相当)。
 */

export const SingularDayForm = ({
  mode,
  register,
  control,
  errors,
  dateValue,
  onDateChange,
  onEditGroupNames,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { groupNames: namesByGroup } = useSingularGroupNameMap()

  // 需要扱い select の選択肢: 平日/土曜/日祝相当 (フラット) + optgroup
  // 「特異グループから選択」に特異グループ1〜10 (design
  // special-day-calendar.html L269-277 準拠。 竹モック `_editBehaviorKey`
  // 同型)。 選択値は demandBehavior + singularGroup を合成した proxy 値
  // (`encodeDemandBehaviorProxy` / `decodeDemandBehaviorProxy`)。
  const demandBehaviorProxyOptions: DsSelectOptions = [
    {
      value: 'weekday',
      label: t(
        singularDayDemandBehaviorLabelKey(SingularDayDemandBehavior.WEEKDAY),
      ),
    },
    {
      value: 'saturday',
      label: t(
        singularDayDemandBehaviorLabelKey(SingularDayDemandBehavior.SATURDAY),
      ),
    },
    {
      value: 'holiday',
      label: t(
        singularDayDemandBehaviorLabelKey(SingularDayDemandBehavior.HOLIDAY),
      ),
    },
    {
      label: t('page:adminSingularDay.demandBehavior.singularGroupOptgroup'),
      // 命名済みグループは保存された表示名、 未命名は既定名「特異グループN」
      // (AC-MSD-011 ※注記)。
      options: SINGULAR_DAY_SPECIAL_GROUP_NUMBERS.map((n) => ({
        value: `singular-${n}`,
        label: resolveSingularGroupName(n, namesByGroup, t),
      })),
    },
  ]

  return (
    <Grid
      alignItems="center"
      gridRowGap={3}
      gridColumnGap={4}
      gridTemplateColumns="max-content 1fr"
    >
      <GridFormLabel htmlFor="singularDay-date">
        {t('page:adminSingularDay.form.date')}
      </GridFormLabel>
      <FieldRoot>
        <CompactCivilDateField
          disabled={mode === 'edit'}
          id="singularDay-date"
          onChange={(d) => {
            onDateChange?.(d)
          }}
          value={dateValue}
        />
      </FieldRoot>

      <GridFormLabel htmlFor="singularDay-name" isTop>
        {t('page:adminSingularDay.form.name')}
      </GridFormLabel>
      {/* `alignItems="stretch"`: DS `Input` は内部を縦 Stack + `Field.Root` で
          包むが、 chakra の `Field.Root` は `align-items: flex-start` なので
          その Stack がセル幅まで伸びず内容幅で止まり、 中の `Input` の
          `width: 100%` も縮んだ親に対して解決されて細くなる
          (Chromium 実測: セル 249px に対し入力欄 177px、 2026-08-27)。
          `BulkCreateModal` の `TextArea` で踏んだのと同じ挙動。
          design (`design/demand/special-day-calendar.html` の
          `.sdc-form .ecl-input { width: 100% }`) は入力列いっぱいに広げる。 */}
      <FieldRoot alignItems="stretch" invalid={errors.name !== undefined}>
        {/* AC-08 の 30 文字超過時保存拒否を到達可能にするため maxLength
            (hard cap) は付けず soft limit にする。 */}
        <Input
          width="full"
          id="singularDay-name"
          placeholder={t('page:adminSingularDay.form.namePlaceholder')}
          {...register('name')}
        />
        {errors.name !== undefined && (
          <FieldErrorText>{errors.name.message}</FieldErrorText>
        )}
      </FieldRoot>

      <GridFormLabel htmlFor="singularDay-area" isTop>
        {t('page:adminSingularDay.form.area')}
      </GridFormLabel>
      <Controller
        control={control}
        name="areaCodes"
        render={({ field: { value, onChange } }) => (
          <FieldRoot invalid={errors.areaCodes !== undefined}>
            <MultiSelect
              aria-label={t('page:adminSingularDay.form.area')}
              inputId="singularDay-area"
              onChange={(next) => {
                onChange(next.map(Number))
              }}
              options={AREA_CODE_OPTIONS.map((code) => ({
                value: String(code),
                label: convertAreaLabel(code),
              }))}
              placeholder={t('page:adminSingularDay.form.areaPlaceholder')}
              selected={(value ?? []).map(String)}
            />
            {errors.areaCodes !== undefined && (
              <FieldErrorText>{errors.areaCodes.message}</FieldErrorText>
            )}
            <FieldHelperText>
              {t('page:adminSingularDay.form.areaHint')}
            </FieldHelperText>
          </FieldRoot>
        )}
      />

      <GridFormLabel htmlFor="singularDay-category">
        {t('page:adminSingularDay.form.category')}
      </GridFormLabel>
      {/* `alignItems="stretch"`: 名称欄と同じ理由 (DS の select も内部 Stack が
      `Field.Root` の `align-items: flex-start` で伸びない)。 design
      `.sdc-form .ecl-select { width: 100% }` に合わせ入力列いっぱいにする。 */}
      <FieldRoot alignItems="stretch" invalid={errors.category !== undefined}>
        <FormSelect
          aria-label={t('page:adminSingularDay.form.category')}
          control={control}
          // 2 カラム grid では label が sibling (`GridFormLabel` の `htmlFor`) なので、
          // field context ではなく明示的な id で関連付ける。
          inputId="singularDay-category"
          name="category"
          numeric
          options={SINGULAR_DAY_CATEGORY_OPTIONS.map((c) => ({
            value: String(c),
            label: t(singularDayCategoryLabelKey(c)),
          }))}
        />
        {errors.category !== undefined && (
          <FieldErrorText>{errors.category.message}</FieldErrorText>
        )}
      </FieldRoot>

      <GridFormLabel htmlFor="singularDay-demandBehavior" isTop>
        {t('page:adminSingularDay.form.demandBehavior')}
      </GridFormLabel>
      {/* 需要扱い select は demandBehavior + singularGroup の2 form field を
          1 つの proxy 値へ合成して表示する (design Note
          singular-day-special-group.md D4)。 FormSelect は単一 field しか
          橋渡ししないため、 ここでは DsSelect を直接 2 段の Controller で
          包んで両 field を同時に読み書きする。 */}
      <Controller
        control={control}
        name="demandBehavior"
        render={({ field: demandBehaviorField }) => (
          <Controller
            control={control}
            name="singularGroup"
            render={({ field: singularGroupField }) => (
              // `alignItems="stretch"`: 名称欄と同じ理由 (DS の select も内部 Stack が
              // `Field.Root` の `align-items: flex-start` で伸びない)。 design
              // `.sdc-form .ecl-select { width: 100% }` に合わせ入力列いっぱいにする。
              <FieldRoot
                alignItems="stretch"
                invalid={errors.demandBehavior !== undefined}
              >
                {/* design `.sdc-behavior-row`: 需要扱い select の右端に
                    「特異グループ名を編集」 を並べる
                    (`special-day-calendar.html:192-195`)。 */}
                <Flex alignItems="center" gap={2}>
                  <Box flex="1 1 auto" minWidth={0}>
                    <DsSelect
                      aria-label={t(
                        'page:adminSingularDay.form.demandBehavior',
                      )}
                      inputId="singularDay-demandBehavior"
                      onBlur={demandBehaviorField.onBlur}
                      onChange={(raw) => {
                        if (raw === undefined) {
                          return
                        }
                        const decoded = decodeDemandBehaviorProxy(raw)
                        demandBehaviorField.onChange(decoded.demandBehavior)
                        singularGroupField.onChange(decoded.singularGroup)
                      }}
                      options={demandBehaviorProxyOptions}
                      value={encodeDemandBehaviorProxy(
                        demandBehaviorField.value,
                        singularGroupField.value,
                      )}
                    />
                  </Box>
                  {/* すべての特異グループ名をまとめて編集する導線。 8080 は
                      この 1 箇所に集約している (一覧のツールバーには置かない)。
                      個別の名前は下のインライン欄で直せるので、ここは「他の
                      グループも見直したい」時の入口。 */}
                  {onEditGroupNames !== undefined && (
                    <TertiaryButton
                      flexShrink={0}
                      whiteSpace="nowrap"
                      onClick={onEditGroupNames}
                      /* [2026-09-17 / Des 指摘] モーダル内のボタンは m で統一。
                         隣の需要扱い select (40px) と高さを揃える。 */
                      size="md"
                    >
                      {t('page:adminSingularDay.toolbar.editGroupNames')}
                    </TertiaryButton>
                  )}
                </Flex>
                {errors.demandBehavior !== undefined && (
                  <FieldErrorText>
                    {errors.demandBehavior.message}
                  </FieldErrorText>
                )}
                <FieldHelperText>
                  {t('page:adminSingularDay.form.demandBehaviorHint')}
                </FieldHelperText>
                {/* [2026-09-17 / Des 指摘] 特異グループ選択時のインライン命名欄
                    (旧 `SingularGroupNameInlineEditor`) を撤去した。 同じ行の
                    「特異グループ名を編集」 から開く一括エディタ (AC-MSD-011d) で
                    同じことができ、 残すと**1 つのモーダルに保存ボタンが 2 つ**
                    (特異日の「保存」 と グループ名の「グループ名を保存」) 並んで
                    どちらが何を保存するのか読み取れないため。
                    ※ AC-MSD-011c は「その場で入力・変更できる欄」 を P0 で要求して
                    いるので、 spec 側の更新が要る (PdM へ issue で依頼済み)。 */}
              </FieldRoot>
            )}
          />
        )}
      />
    </Grid>
  )
}
